/**
 * pages/builder/placeholders.jsx
 * ------------------------------
 * Builder-route pages that surface in the nav for full UX completeness
 * but defer to existing flows or to "Coming Soon" cards until the
 * dedicated backend lands.
 *
 * - DealerSubmit / BuilderProjectsTab reuse → don't duplicate logic.
 * - Brochures / Updates / Visits → backend-pending placeholders that
 *   link to the closest live capability so builders never hit a dead
 *   surface.
 *
 * v2.71.43 — Initial.
 */

import React, { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import {
  FileText, Calendar, ListChecks, ArrowRight, BarChart3, Bell, User,
} from "lucide-react";
import { DashboardLayout } from "../../features/dashboard";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";

const BuilderProjectsTab = lazy(() => import("../BuilderProjectsTab"));

/* ────────────────────────────────────────────────────────────────────── */
/*  /builder/projects — uses existing BuilderProjectsTab inside layout    */
/* ────────────────────────────────────────────────────────────────────── */

export function BuilderProjectsRoute() {
  return (
    <DashboardLayout title="My Projects" subtitle="Portfolio of your developments">
      <Suspense fallback={<Skeleton className="w-full h-[480px] rounded-sm" />}>
        <BuilderProjectsTab readOnly={false} />
      </Suspense>
    </DashboardLayout>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  Generic builder placeholder card                                      */
/* ────────────────────────────────────────────────────────────────────── */

function PlaceholderCard({ icon: Icon, title, body, primaryTo, primaryLabel, testid }) {
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm p-8 sm:p-10 text-center" data-testid={testid}>
      <div className="w-12 h-12 mx-auto bg-[#EEF4FF] rounded-full flex items-center justify-center text-[#0055FF] mb-3">
        <Icon size={20} />
      </div>
      <h3 className="font-heading font-semibold text-lg tracking-tight">{title}</h3>
      <p className="text-[13px] text-[#52525B] mt-1.5 max-w-md mx-auto">{body}</p>
      {primaryTo && primaryLabel ? (
        <Link to={primaryTo} className="inline-block mt-4">
          <Button size="sm" className="btn-brand rounded-sm">
            {primaryLabel} <ArrowRight size={12} className="ml-1.5" />
          </Button>
        </Link>
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  /builder/brochures — media library                                    */
/* ────────────────────────────────────────────────────────────────────── */

export function BuilderBrochuresRoute() {
  return (
    <DashboardLayout title="Brochures & Media" subtitle="PDF brochures, walkthrough videos, gallery">
      <PlaceholderCard
        icon={FileText}
        title="Centralized media library coming soon"
        body="Upload brochures and walkthrough videos once and reuse them across every project. For now, attach media directly to each project from the project workspace."
        primaryTo="/builder/projects"
        primaryLabel="Open Projects"
        testid="builder-brochures-placeholder"
      />
    </DashboardLayout>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  /builder/updates — project update timeline                            */
/* ────────────────────────────────────────────────────────────────────── */

export function BuilderUpdatesRoute() {
  return (
    <DashboardLayout title="Project Updates" subtitle="Construction milestones · price changes · launches">
      <PlaceholderCard
        icon={ListChecks}
        title="Project update feed coming soon"
        body="Post construction milestones, price changes, or launch announcements that surface on buyer-facing project pages. Updates increase inquiry quality."
        primaryTo="/builder/projects"
        primaryLabel="Edit a project"
        testid="builder-updates-placeholder"
      />
    </DashboardLayout>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  /builder/visits — site visit schedule                                 */
/* ────────────────────────────────────────────────────────────────────── */

export function BuilderVisitsRoute() {
  return (
    <DashboardLayout title="Site Visits" subtitle="Visits routed by HouseEntry call team">
      <PlaceholderCard
        icon={Calendar}
        title="Site visits dashboard coming soon"
        body="HouseEntry's call team schedules visits on your behalf. Once your dedicated visit calendar ships, scheduled tours and feedback will appear here."
        primaryTo="/builder/inquiries"
        primaryLabel="View inquiry pipeline"
        testid="builder-visits-placeholder"
      />
    </DashboardLayout>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  /builder/analytics — perf overview (admin-controlled scope)            */
/* ────────────────────────────────────────────────────────────────────── */

export function BuilderAnalyticsRoute() {
  return (
    <DashboardLayout title="Performance" subtitle="Project views · inquiry conversion · time-to-close">
      <PlaceholderCard
        icon={BarChart3}
        title="Project performance dashboard coming soon"
        body="View counts, inquiry-to-visit conversion, and average time-to-deal per project. Aggregate stats only — customer identity stays with HouseEntry."
        primaryTo="/builder/overview"
        primaryLabel="Back to overview"
        testid="builder-analytics-placeholder"
      />
    </DashboardLayout>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  /builder/notifications — admin-routed events                          */
/* ────────────────────────────────────────────────────────────────────── */

export function BuilderNotificationsRoute() {
  return (
    <DashboardLayout title="Notifications" subtitle="Approval status · routed inquiries · admin announcements">
      <PlaceholderCard
        icon={Bell}
        title="Notification center coming soon"
        body="Project approvals, new inquiries routed to you, and admin announcements will appear here. Until then, urgent updates are emailed."
        primaryTo="/builder/inquiries"
        primaryLabel="Open inquiries"
        testid="builder-notifications-placeholder"
      />
    </DashboardLayout>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  /builder/profile — link out to /profile                                */
/* ────────────────────────────────────────────────────────────────────── */

export function BuilderProfileRoute() {
  return (
    <DashboardLayout title="Profile" subtitle="Manage your account">
      <PlaceholderCard
        icon={User}
        title="Update your builder profile"
        body="Edit your name, phone, password, and email preferences in the main profile screen — the same identity is used across the builder console."
        primaryTo="/profile"
        primaryLabel="Open Profile"
        testid="builder-profile-placeholder"
      />
    </DashboardLayout>
  );
}
